import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { ContentCopy, Timeline } from "@mui/icons-material"
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router-dom"
import { Sstv2TermChip } from "./Sstv2TermLabel"
import {
  Sstv2MigrationRow,
  computeStall,
  formatDuration,
  formatStreamsCell,
  hasReservationFailures,
  reservationFailureCount,
  phaseChipColor,
  phaseLabelKey,
} from "./sstv2MigrationTypes"
import { describePhase } from "./sstv2Vocabulary"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

const PHASE_OPTIONS = [
  "ALL",
  "ACTIVE_MIGRATED",
  "OPERATIONAL",
  "COMPLETED",
  "ROLLBACK_STARTED",
  "ROLLED_BACK",
] as const

type PhaseFilter = (typeof PHASE_OPTIONS)[number]

/** Deep-link to every trace of one migration (tab 6 = Live traces). */
const tracesUrlFor = (migrationId: string) =>
  `/sa/sstv2?tab=6&migrationId=${encodeURIComponent(migrationId)}`

export const Sstv2MigrationGrid: FC<{
  rows: Sstv2MigrationRow[]
  loading: boolean
  onRowClick: (row: Sstv2MigrationRow) => void
  /** Reference "now" (ms) for stall/elapsed calc — ticks under polling. */
  nowMs: number
}> = ({ rows, loading, onRowClick, nowMs }) => {
  const { t } = useTranslation()

  const [search, setSearch] = useState("")
  const [phase, setPhase] = useState<PhaseFilter>("ALL")
  const [tenant, setTenant] = useState<string>("ALL")
  const [openOnly, setOpenOnly] = useState(false)
  const [stalledOnly, setStalledOnly] = useState(false)

  const tenantOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of rows) map.set(r.tenantId, r.tenantName)
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [rows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = rows.filter((r) => {
      // "Open only" = still moving: neither COMPLETED nor rolled back. This is the
      // default working set during a migration window.
      if (openOnly && (r.phase === "COMPLETED" || r.isRollback)) return false
      if (stalledOnly && !computeStall(r.phase, r.lastEventAt, nowMs).isStalled)
        return false
      if (phase !== "ALL" && r.phase !== phase) return false
      if (tenant !== "ALL" && r.tenantId !== tenant) return false
      if (q) {
        // Migration id first — pasting one from a log/trace must find its row.
        const hay = [r.migrationId, r.sl1LockerId, r.v1LockerName, r.tenantName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
    // Newest attempt first — the one an operator is watching right now. Sorted
    // here rather than via DataGrid's sortModel because `startedAt` is not a
    // rendered column (it reads as the attempt hint under the locker name).
    return [...list].sort((a, b) => b.startedAt.localeCompare(a.startedAt))
  }, [rows, search, phase, tenant, openOnly, stalledOnly, nowMs])

  const copyMigrationId = useCallback((migrationId: string) => {
    void navigator.clipboard?.writeText(migrationId)
  }, [])

  const columns: GridColDef<Sstv2MigrationRow>[] = useMemo(
    () => [
      {
        field: "migrationId",
        headerName: t("migrationId"),
        width: 250,
        renderCell: (p: GridRenderCellParams<Sstv2MigrationRow>) => {
          const migrationId = p.row.migrationId
          // A record written before the 2026-07-28 re-key has no id. It is not a
          // job we can address (no traces to link, nothing to copy), so say so
          // instead of rendering a broken affordance — and never crash the grid.
          if (!migrationId) {
            return (
              <Tooltip title={t("migrationLegacyRecordHint")}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("migrationLegacyRecord")}
                  sx={{ height: 20, fontSize: "0.68rem" }}
                />
              </Tooltip>
            )
          }
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={migrationId}>
                <Typography variant="body2" fontFamily="monospace" noWrap>
                  {migrationId.slice(0, 12)}…
                </Typography>
              </Tooltip>
              <Tooltip title={t("copy")}>
                <IconButton
                  size="small"
                  aria-label={t("copy")}
                  onClick={(e) => {
                    e.stopPropagation()
                    copyMigrationId(migrationId)
                  }}
                >
                  <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("migrationViewTraces")}>
                <IconButton
                  size="small"
                  component={RouterLink}
                  to={tracesUrlFor(migrationId)}
                  aria-label={t("migrationViewTraces")}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Timeline sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      {
        field: "v1LockerName",
        headerName: t("migrationLocker"),
        flex: 1,
        minWidth: 170,
        renderCell: (p: GridRenderCellParams<Sstv2MigrationRow>) => (
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {p.row.v1LockerName}
            </Typography>
            {p.row.attemptCount > 1 && (
              <Typography variant="caption" color="text.secondary">
                {t("migrationAttemptNofM", {
                  n: p.row.attemptIndex,
                  m: p.row.attemptCount,
                })}
              </Typography>
            )}
          </Stack>
        ),
      },
      { field: "tenantName", headerName: t("tenantName"), flex: 1, minWidth: 130 },
      {
        field: "phase",
        headerName: t("migrationPhase"),
        width: 165,
        renderCell: (p: GridRenderCellParams<Sstv2MigrationRow>) => (
          <Sstv2TermChip
            term={describePhase(p.row.phase)}
            color={phaseChipColor(p.row.phase)}
          />
        ),
      },
      {
        field: "reservations",
        headerName: t("migrationReservations"),
        width: 175,
        sortable: false,
        renderCell: (p: GridRenderCellParams<Sstv2MigrationRow>) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={t("migrationStreamsHint")}>
              <Typography variant="body2" fontFamily="monospace">
                {formatStreamsCell(p.row)}
              </Typography>
            </Tooltip>
            {hasReservationFailures(p.row) && (
              <Chip
                size="small"
                color="error"
                label={reservationFailureCount(p.row)}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            )}
          </Stack>
        ),
      },
      {
        field: "timeSinceLastEvent",
        headerName: t("migrationTimeSinceEvent"),
        width: 155,
        // Sort by elapsed minutes; terminal rows sort as -1 (not stalled).
        valueGetter: (_v, row) => {
          const s = computeStall(row.phase, row.lastEventAt, nowMs)
          return s.terminal ? -1 : s.minutesSinceLastEvent
        },
        renderCell: (p: GridRenderCellParams<Sstv2MigrationRow>) => {
          const s = computeStall(p.row.phase, p.row.lastEventAt, nowMs)
          if (s.terminal) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )
          }
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {formatDuration(s.minutesSinceLastEvent)}
              </Typography>
              {s.isStalled && (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={t("migrationStalled")}
                  sx={{ height: 20, fontSize: "0.68rem" }}
                />
              )}
            </Stack>
          )
        },
      },
      {
        field: "lastEventType",
        headerName: t("migrationLastEvent"),
        flex: 1,
        minWidth: 210,
        renderCell: (p: GridRenderCellParams<Sstv2MigrationRow>) => (
          <Typography variant="caption" fontFamily="monospace" noWrap>
            {p.row.lastEventType}
          </Typography>
        ),
      },
      {
        field: "createdSl2LockerThingId",
        headerName: t("migrationV2Locker"),
        width: 120,
        sortable: false,
        renderCell: (p: GridRenderCellParams<Sstv2MigrationRow>) =>
          p.row.createdSl2LockerThingId ? (
            <Link
              component={RouterLink}
              to={`/sa/lockers/${p.row.createdSl2LockerThingId}`}
              onClick={(e) => e.stopPropagation()}
            >
              {t("viewLocker")}
            </Link>
          ) : (
            "—"
          ),
      },
    ],
    [t, nowMs, copyMigrationId],
  )

  return (
    <Box data-ui="UI-SL2W-256" sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        rowGap={1.5}
        sx={{ mb: 2 }}
      >
        <TextField
          label={t("migrationSearchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 300 }}
        />
        <TextField
          select
          label={t("migrationPhase")}
          value={phase}
          onChange={(e) => setPhase(e.target.value as PhaseFilter)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {PHASE_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              {p === "ALL"
                ? t("all")
                : t(phaseLabelKey(p as Sstv2MigrationRow["phase"]))}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={t("tenantName")}
          value={tenant}
          onChange={(e) => setTenant(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ALL">{t("all")}</MenuItem>
          {tenantOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.name}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={openOnly}
              onChange={(e) => setOpenOnly(e.target.checked)}
            />
          }
          label={t("migrationOpenOnly")}
        />
        <FormControlLabel
          control={
            <Switch
              checked={stalledOnly}
              onChange={(e) => setStalledOnly(e.target.checked)}
              color="error"
            />
          }
          label={t("migrationStalledOnly")}
        />
      </Stack>

      <DataGrid
        autoHeight
        rows={filtered}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        onRowClick={(p) => onRowClick(p.row as Sstv2MigrationRow)}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        getRowClassName={(p) =>
          (p.row as Sstv2MigrationRow).isRollback ? "sstv2-rollback-row" : ""
        }
        sx={{
          border: "none",
          cursor: "pointer",
          "& .sstv2-rollback-row": {
            backgroundColor: "rgba(211, 47, 47, 0.06)",
            borderLeft: "3px solid",
            borderLeftColor: "error.main",
          },
          "& .sstv2-rollback-row:hover": {
            backgroundColor: "rgba(211, 47, 47, 0.12)",
          },
        }}
      />
    </Box>
  )
}

export default Sstv2MigrationGrid
